import { ORDERS_WS } from "@/lib/backend";
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Users, UtensilsCrossed, MessageSquare,
  ClipboardList, LogOut, Menu, X, ChevronRight, TrendingUp, Settings
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import api, { updateOrder } from "@/lib/api";
import { printReceipt } from "@/lib/printReceipt";

// =====================================================================
// MEMÓRIA GLOBAL ABSOLUTA (Fica fora do React para evitar duplicidade)
// =====================================================================
const printedOrdersMemory = new Set();

const navItems =[
  { path: "/dashboard", label: "Painel", icon: LayoutDashboard },
  { path: "/orders", label: "Pedidos", icon: ClipboardList },
  { path: "/customers", label: "Clientes", icon: Users },
  { path: "/menu", label: "Cardapio", icon: UtensilsCrossed },
  { path: "/faturamento", label: "Faturamento", icon: TrendingUp },
  { path: "/settings", label: "Configurações", icon: Settings },
];

export default function Layout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const[mobileOpen, setMobileOpen] = useState(false);
  const user = JSON.parse(localStorage.getItem("user") || "{}");

  // ==================== WEBSOCKET GLOBAL ====================
  useEffect(() => {
    let ws;
    let reconnectTimeout;
    let pingInterval;
    let disposed = false;

    const connectWebSocket = () => {
      if (disposed) return;
      ws = new WebSocket(ORDERS_WS);

      ws.onopen = () => {
        if (disposed) {
          ws.close();
          return;
        }
        console.log("🟢 Conectado ao Radar de Pedidos em Tempo Real");

        const token = localStorage.getItem("token") || sessionStorage.getItem("token");
        ws.send(JSON.stringify({ token }));
        clearInterval(pingInterval);
        pingInterval = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send("ping");
          }
        }, 30000);
      };

      ws.onmessage = async (event) => {
        const data = JSON.parse(event.data);
        
        if (data.type === "NOVO_PEDIDO") {
          const novoPedido = data.pedido;
          const isUpdate = data.is_update === true;

          // Atualiza a tela com o proprio payload recebido. A tela nao precisa
          // consultar novamente todo o historico para descobrir este pedido.
          window.dispatchEvent(new CustomEvent("pedidoAtualizado", {
            detail: { pedido: novoPedido, isUpdate }
          }));

          if (novoPedido.Impresso !== "false" && novoPedido.Impresso !== false) return;
          if (!Number.isInteger(novoPedido.versao) || novoPedido.versao < 1) return;
          const printKey = `impresso_${novoPedido.id}`;
          const memoryKeys = [`${novoPedido.id}:${novoPedido.versao}`];
          if (printedOrdersMemory.has(memoryKeys[0])) return;
          const previous = localStorage.getItem(printKey);
          if (previous?.startsWith("v_") && Number(previous.split("_")[1]) >= novoPedido.versao) return;
          let lockValue = `v_${novoPedido.versao}_${Date.now()}_${Math.random()}`;
          localStorage.setItem(printKey, lockValue);
          await new Promise(resolve => setTimeout(resolve, 50));
          if (localStorage.getItem(printKey) !== lockValue) return;
          printedOrdersMemory.add(memoryKeys[0]);
          let dialogOpened = false;
          try {
              const response = await api.get(`/orders/${novoPedido.id}/print`);
              if (disposed || localStorage.getItem(printKey) !== lockValue) throw new Error("Reserva de impressão encerrada");
              if (!Number.isInteger(response.data.versao) || response.data.versao < novoPedido.versao) throw new Error("Versão de impressão divergente");
              memoryKeys.push(`${novoPedido.id}:${response.data.versao}`);
              printedOrdersMemory.add(memoryKeys[1]);
              lockValue = `v_${response.data.versao}_${Date.now()}_${Math.random()}`;
              localStorage.setItem(printKey, lockValue);
              if (response.data.Impresso === "true" || response.data.Impresso === true) return;
              await printReceipt(response.data.html_comanda, () => !disposed && localStorage.getItem(printKey) === lockValue);
              dialogOpened = true;
              const printed = await updateOrder(response.data, { Impresso: "true" });
              window.dispatchEvent(new CustomEvent("pedidoAtualizado", {
                detail: { pedido: printed.data, isUpdate: true }
              }));
          } catch (err) {
              console.error("Erro ao carregar ou marcar a comanda como impressa", err);
              // Depois de print(), ACK incerto não autoriza abrir outra impressão automaticamente.
              if (!dialogOpened) {
                if (localStorage.getItem(printKey) === lockValue) localStorage.removeItem(printKey);
                memoryKeys.forEach(key => printedOrdersMemory.delete(key));
              }
          }
        }
      };

      ws.onclose = () => {
        clearInterval(pingInterval);
        if (disposed) return;
        console.log("🔴 Conexão perdida. Tentando reconectar em 3 segundos...");
        reconnectTimeout = setTimeout(connectWebSocket, 3000);
      };

      ws.onerror = (err) => {
        console.error("Erro no WebSocket", err);
        ws.close();
      };
    };

    connectWebSocket();

    return () => {
      disposed = true;
      clearTimeout(reconnectTimeout);
      clearInterval(pingInterval);
      if (ws) {
        // Impede que o close intencional agende uma nova conexao fantasma.
        ws.onopen = null;
        ws.onmessage = null;
        ws.onerror = null;
        ws.onclose = null;
        ws.close();
      }
    };
  },[]);
  
  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    sessionStorage.removeItem("token");
    sessionStorage.removeItem("user");
    navigate("/");
  };

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="px-4 py-6 flex items-center gap-3">
        <img 
          src="/logo.png"
          alt="Logo SuperPizza" 
          className="w-8 h-8 object-contain" 
        />
        {!collapsed && (
          <div className="overflow-hidden">
            <h1 className="text-lg font-bold tracking-tight text-foreground uppercase" style={{ fontFamily: 'Oswald, sans-serif' }}>
              SuperPizza
            </h1>
            <p className="text-[10px] tracking-[0.2em] text-muted-foreground uppercase">
              CRM Pizzaria
            </p>
          </div>
        )}
      </div>

      <Separator className="bg-border" />

      {/* Nav */}
      <nav className="flex-1 px-3 py-4 space-y-1">
        {navItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <button
              key={item.path}
              data-testid={`nav-${item.path.slice(1)}`}
              onClick={() => {
                navigate(item.path);
                setMobileOpen(false);
              }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-all duration-200 group
                ${isActive
                  ? "bg-primary/15 text-primary border-l-2 border-primary"
                  : "text-muted-foreground hover:text-foreground hover:bg-secondary/60 border-l-2 border-transparent"
                }`}
            >
              <item.icon className={`w-4 h-4 shrink-0 ${isActive ? "text-primary" : "text-muted-foreground group-hover:text-foreground"}`} />
              {!collapsed && <span>{item.label}</span>}
              {!collapsed && isActive && <ChevronRight className="w-3 h-3 ml-auto text-primary" />}
            </button>
          );
        })}
      </nav>

      <Separator className="bg-border" />

      {/* User */}
      <div className="px-3 py-4">
        {!collapsed && (
          <div className="px-3 mb-3">
            <p className="text-sm font-medium text-foreground truncate">{user.name || "Admin"}</p>
            <p className="text-xs text-muted-foreground truncate">{user.email || ""}</p>
          </div>
        )}
        <Button
          data-testid="logout-btn"
          variant="ghost"
          size="sm"
          onClick={handleLogout}
          className="w-full justify-start gap-2 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
        >
          <LogOut className="w-4 h-4" />
          {!collapsed && "Sair"}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-background noise-bg overflow-hidden">
      {/* Desktop Sidebar */}
      <aside
        className={`hidden lg:flex flex-col border-r border-border bg-card transition-all duration-300 ${
          collapsed ? "w-16" : "w-60"
        }`}
      >
        <SidebarContent />
        <button
          data-testid="sidebar-toggle"
          onClick={() => setCollapsed(!collapsed)}
          className="p-2 m-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
        >
          <Menu className="w-4 h-4" />
        </button>
      </aside>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/60" onClick={() => setMobileOpen(false)} />
          <aside className="relative w-60 h-full bg-card border-r border-border">
            <SidebarContent />
          </aside>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 flex flex-col overflow-hidden">
        {/* Mobile Header */}
        <header className="lg:hidden flex items-center gap-3 px-4 py-3 border-b border-border bg-card">
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setMobileOpen(true)}
            className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary"
          >
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md bg-primary flex items-center justify-center">
              <UtensilsCrossed className="w-4 h-4 text-primary-foreground" />
            </div>
            <span className="text-sm font-bold uppercase tracking-tight" style={{ fontFamily: 'Oswald, sans-serif' }}>
              SuperPizza
            </span>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto p-4 md:p-6 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
}
